"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RequestCustomDemoSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section className="bg-background py-24">
      <div className="container mx-auto px-6 max-w-2xl">
        <FormProvider className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-out)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2.5"}`} formId="request-demo-form-cta" sectionName="request-custom-demo-section" defaultValues={{
        fullName: "",
        email: "",
        companyName: "",
        phone: "",
        website: ""
      }} validationRules={{
        fullName: {
          required: true,
          minLength: 2,
          maxLength: 50
        },
        email: {
          required: true,
          pattern: "email"
        },
        companyName: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        phone: {},
        website: {}
      }} submitText="Kostenlose KI-Demo anfordern" successMessage="Vielen Dank! Wir werden uns in Kürze bei Ihnen melden, um Ihre individuelle KI-Demo einzurichten.">
          <div ref={ref}>
            <DemoRequestForm />
          </div>
        </FormProvider>
      </div>
    </section>;
}
function DemoRequestForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="text-center shadow-medium">
        <CardContent className="py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-light mb-6">
            <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-xl text-foreground mb-6">{successMessage}</p>
          <Button onClick={resetForm} variant="outline" size="lg" className="text-base">
            Weitere Demo anfordern
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="shadow-medium">
      <CardContent className="py-10">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-display font-bold text-foreground mb-3">Individuelle Demo sehen</h2>
          <p className="text-muted-foreground text-lg">
            Erleben Sie JETZT ihren persönlichen Virtuellen Asisstenten in Aktion!&nbsp;
          </p>
        </div>

        <Form onSubmit={handleSubmit}>
          <FormField control={control} name="fullName" rules={{
          required: "Name ist erforderlich",
          minLength: {
            value: 2,
            message: "Mindestens 2 Zeichen erforderlich"
          },
          maxLength: {
            value: 50,
            message: "Maximal 50 Zeichen erlaubt"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-base">Name</FormLabel>
                <FormControl>
                  <Input placeholder="Ihr Name" className="h-12 text-lg transition-all duration-[var(--duration-fast)] focus-visible:ring-primary/50 focus-visible:shadow-soft" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "E-Mail ist erforderlich",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Ungültige E-Mail-Adresse"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-base">E-Mail</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="Ihre E-Mail" className="h-12 text-lg transition-all duration-[var(--duration-fast)] focus-visible:ring-primary/50 focus-visible:shadow-soft" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="companyName" rules={{
          required: "Firmenname ist erforderlich",
          minLength: {
            value: 2,
            message: "Mindestens 2 Zeichen erforderlich"
          },
          maxLength: {
            value: 100,
            message: "Maximal 100 Zeichen erlaubt"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-base">Firmenname</FormLabel>
                <FormControl>
                  <Input placeholder="Ihr Firmenname" className="h-12 text-lg transition-all duration-[var(--duration-fast)] focus-visible:ring-primary/50 focus-visible:shadow-soft" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-base">Telefonnummer (optional für Rückruf)</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="Ihre Telefonnummer" className="h-12 text-lg transition-all duration-[var(--duration-fast)] focus-visible:ring-primary/50 focus-visible:shadow-soft" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="website" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-base">Website-Link (optional)</FormLabel>
                <FormControl>
                  <Input type="url" placeholder="Link zur Unternehmenswebsite" className="h-12 text-lg transition-all duration-[var(--duration-fast)] focus-visible:ring-primary/50 focus-visible:shadow-soft" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-red-800 dark:text-red-200 text-base">{submitError}</p>
            </div>}

          <Button type="submit" disabled={isSubmitting} size="lg" className="w-full h-12 text-lg font-semibold shadow-medium transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)] hover:scale-[1.02] hover:shadow-strong active:scale-100 active:shadow-medium">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-5" />
                Wird gesendet...
              </> : "Kostenlose KI-Demo anfordern"}
          </Button>
        </Form>
      </CardContent>
    </Card>;
}